import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  ActionButton,
  Buttons,
  CancelButton,
  Checkbox,
  Drawer,
  Form,
  Icon,
  Tooltip,
  TooltipButton,
} from '~/components';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import MemberTemplateSelect from '~/components/MemberTemplateSelect';
import SecurityRoleSelect from '~/components/SecurityRoleSelect';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import { colors } from '~/styles';

const ManagerWarning = styled(Tooltip)`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
  margin-left: 0.5rem;
`;

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;

  > label:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

export default function MicrosoftSignInDrawer({ signIn: initialSignIn, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const [signIn, setSignIn] = useState(initialSignIn);
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [memberTemplates, setMemberTemplates] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).memberTemplates().get({ size: 1000 });
        setMemberTemplates(data);
      } catch {
        setMemberTemplates([]);
      }
    })();
  }, [api, workspace.id]);

  const handleChange = (event) => {
    setDirty(true);
    setSignIn((signIn) => ({ ...signIn, [event.target.name]: event.target.checked }));
  };

  const handleSelect = ({ target: { name, value } }) => {
    setDirty(true);
    setSignIn((signIn) => ({ ...signIn, [name]: value || null }));
  };

  return (
    <Drawer
      isOpen
      title="Microsoft Single Sign-On"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            await api.www.workspaces(workspace.id).integrations().microsoft.connection.updateSignIn(signIn);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        const signInLink = `${window.location.origin}/login/microsoft/${workspace.key}`;

        return (
          <>
            <Form.Section title="Microsoft Single Sign-On">
              <Form.Control>
                <Checkboxes>
                  <Checkbox
                    name="enabled"
                    label="Enable Microsoft Single Sign-On"
                    checked={signIn.enabled}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="isDefault"
                    label="Make it the default sign-in method"
                    checked={signIn.isDefault}
                    disabled={!signIn.enabled}
                    onChange={handleChange}
                  />
                  <Checkbox
                    name="autoProvisionMembers"
                    label="Create members on first sign-in"
                    checked={signIn.autoProvisionMembers}
                    disabled={!signIn.enabled}
                    onChange={handleChange}
                  />
                </Checkboxes>
              </Form.Control>
              {signIn.enabled && signIn.autoProvisionMembers && memberTemplates && (
                <Form.Control>
                  {memberTemplates.length > 0 ? (
                    <MemberTemplateSelect
                      name="autoProvisionMemberTemplateId"
                      placeholder="Member Template"
                      templates={memberTemplates}
                      adornment={(template) =>
                        (template.internalExpenseApprovalMode === 'manager' ||
                          (template.timeOffAllowed && template.timeOffApprovalMode === 'manager')) && (
                          <ManagerWarning message="Members created on first sign-in don't have a manager, so time and expense approvals set to Manager in this template are saved with the default approval mode instead. Set the member's approvals after assigning a manager.">
                            <Icon icon="exclamation-triangle" color={colors.warning} />
                          </ManagerWarning>
                        )
                      }
                      value={signIn.autoProvisionMemberTemplateId}
                      onChange={handleSelect}
                    />
                  ) : (
                    <SecurityRoleSelect
                      name="autoProvisionSecurityRoleId"
                      placeholder="Security Role"
                      value={signIn.autoProvisionSecurityRoleId}
                      useDefault={!signIn.autoProvisionSecurityRoleId}
                      onChange={handleSelect}
                    />
                  )}
                </Form.Control>
              )}
            </Form.Section>

            {initialSignIn.enabled && (
              <Form.Section
                title="Sign-in link"
                subtitle="Share this link with members; it signs them in with Microsoft and, when enabled, creates their account.">
                <Form.Control>
                  <ReadTextbox label="Sign-in link" value={signInLink} />
                  <div style={{ flex: 0 }}>
                    <TooltipButton
                      onClick={() => navigator.clipboard.writeText(signInLink)}
                      tooltip="Copy to clipboard">
                      <Icon color={colors.white} icon="clipboard" />
                    </TooltipButton>
                  </div>
                </Form.Control>
              </Form.Section>
            )}

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
